import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { clsx } from 'clsx';
import { useCallback, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { useIsNavigatingBack, useIsNavigatingBackRef } from '../context/FlowContainerContext.tsx';
import { useMainContext } from '../context/MainContext.tsx';
import { useTranslations } from '../hooks/useTranslations.ts';
import { translationKeys } from '../translations/translationKeys.ts';
import { nonSpectreJourney } from '../utils/backgroundImage.ts';
import { ensureFlagsCssLoaded } from '../utils/flagsCss.ts';
import { injectCountryDiallingCodeData } from '../utils/injectCountryDiallingCodeData.ts';
import { baseInputClasses, getContainerClasses, getInputClasses } from '../utils/inputStyles';
import FieldValidationMessage from './FieldValidationMessage.tsx';
import Text from './Text';

console.log('testing Phone.tsx');

type CountryOption = {
  value: string;
};

type EnhancedCountryOption = CountryOption & {
  label: string;
  countryCode: string;
};

type IProps = {
  updateFieldValue: (value: string) => void;
  triggerFieldChange: (value: string) => void;
  value: string;
  label: string;
  countryOptions: CountryOption[];
  validatemessage?: string;
  id?: string;
  name?: string;
  phoneId?: string;
  phoneName?: string;
  countryCodeId?: string;
  countryCodeName?: string;
  fullWidth?: boolean;
  defaultDiallingCode?: string;
};

const getFlagCountryClassName = (option: EnhancedCountryOption) => {
  return `flag_${option.countryCode.toLowerCase()}`;
};

function Phone(props: IProps) {
  const { journeyId } = useMainContext();
  const isNavigatingBack = useIsNavigatingBack();
  const isNavigatingBackRef = useIsNavigatingBackRef();
  const { translate } = useTranslations();
  const enhancedCountryOptions = useMemo(() => {
    // Add more info such as labels and country codes to the list of dialling codes provided by pega
    return injectCountryDiallingCodeData(props.countryOptions);
  }, [props.countryOptions]);

  const { countryCode, phoneNumberWithoutCountryCode } = extractCountryCodeAndNumber(
    props.value,
    enhancedCountryOptions
  );
  const [selectedCountry, setSelectedCountry] = useState(
    enhancedCountryOptions.find((item) => item.value === countryCode) ||
      (props.defaultDiallingCode
        ? enhancedCountryOptions.find((item) => item.value === props.defaultDiallingCode)
        : undefined) ||
      enhancedCountryOptions[0]
  );

  const [displayPhoneNumber, setDisplayPhoneNumber] = useState(phoneNumberWithoutCountryCode);
  const [focused, setFocused] = useState(false);

  const loadFlagsCssOnPhoneInteraction = useCallback(() => {
    void ensureFlagsCssLoaded().catch(() => {
      // Prevent unhandled promise rejections; error already logged in ensureFlagsCssLoaded.
    });
  }, []);

  const dropdownRef = useRef<HTMLDivElement>(null);

  const inputClasses = twMerge(
    clsx(
      getInputClasses(props.fullWidth, 'flex gap-3'),
      focused &&
        '!bg-interactive-surface-focus !border-interactive-outline-focus !text-interactive-content-focus outline-none transition-colors'
    )
  );
  const containerClasses = twMerge(clsx(getContainerClasses({})));
  const optionsContainerClasses = twMerge(
    clsx(
      baseInputClasses,
      'absolute -left-6 z-10 mt-4 max-h-56 w-full overflow-auto py-1 shadow-lg outline-1 data-leave:transition data-leave:duration-100 data-leave:ease-in data-closed:data-leave:opacity-0 sm:text-sm focus:bg-interactive-surface-default focus:border-interactive-outline-default focus:text-interactive-content-default focus:outline-none'
    )
  );

  function onFocus() {
    setFocused(true);
  }

  function onBlur() {
    setFocused(false);
    if (isNavigatingBackRef.current) {
      return;
    }
    const fullPhoneNumber = selectedCountry.value + stripLeadingZero(displayPhoneNumber);
    props.triggerFieldChange(fullPhoneNumber);
  }

  const onSelectedCountryChange = useCallback(
    (country: EnhancedCountryOption) => {
      setSelectedCountry(country);
      const fullPhoneNumber = country.value + stripLeadingZero(displayPhoneNumber);
      props.triggerFieldChange(fullPhoneNumber);
      props.updateFieldValue(fullPhoneNumber);
    },
    [displayPhoneNumber, props]
  );

  const onUpdateTextFieldValue = useCallback(
    (phoneNumber: string) => {
      setDisplayPhoneNumber(phoneNumber);
      const fullPhoneNumber = selectedCountry.value + stripLeadingZero(phoneNumber);
      props.updateFieldValue(fullPhoneNumber);
    },
    [selectedCountry.value, props]
  );

  const onTextChange = useCallback(
    (phoneNumber: string) => {
      const fullPhoneNumber = selectedCountry.value + stripLeadingZero(phoneNumber);
      props.triggerFieldChange(fullPhoneNumber);
    },
    [selectedCountry.value, props]
  );

  return (
    <div className={containerClasses}>
      <label className="!text-base">{props.label}</label>
      <div className={inputClasses} data-error={!!props.validatemessage && !isNavigatingBack}>
        <div className="relative" ref={dropdownRef}>
          <Listbox value={selectedCountry} onChange={onSelectedCountryChange}>
            <div className="relative">
              <div className="flex flex-row">
                <ListboxButton
                  className="flex gap-2 cursor-pointer border-r border-r-interactive-content-default pr-4"
                  onPointerDown={loadFlagsCssOnPhoneInteraction}
                  onFocus={loadFlagsCssOnPhoneInteraction}
                  onKeyDown={loadFlagsCssOnPhoneInteraction}
                >
                  <div className={`w-[25px] flag selected-flag ${getFlagCountryClassName(selectedCountry)}`}></div>
                  <span className="grow-0 shrink-0">{selectedCountry.value}</span>
                  <ChevronUpDownIcon
                    aria-hidden="true"
                    className={clsx(
                      'col-start-1 row-start-1 size-5 self-center justify-self-end sm:size-4',
                      nonSpectreJourney(journeyId) ? 'text-black' : 'text-gray-500 dark:text-gray-400'
                    )}
                  />
                </ListboxButton>
                <Text
                  type="tel"
                  value={displayPhoneNumber}
                  id={props.phoneId || 'phoneNumber'}
                  name={props.phoneName || 'phoneNumber'}
                  className="flex-1 pl-4 !bg-transparent !border-0 !shadow-none !p-0 !pl-4 text-interactive-content-default focus:text-interactive-content-focus !outline-none !ring-0"
                  placeholder={translate(translationKeys.ENTER_PHONE_NUMBER)}
                  updateFieldValue={onUpdateTextFieldValue}
                  triggerFieldChange={onTextChange}
                  nested
                  onFocus={onFocus}
                  onBlur={onBlur}
                  classesMergingType="override"
                />
              </div>
              <ListboxOptions transition className={optionsContainerClasses}>
                {enhancedCountryOptions.map((option) => (
                  <ListboxOption
                    key={option.label}
                    value={option}
                    className="group relative cursor-pointer gap-2 px-3 py-2 hover:bg-interactive-surface-hover hover:border-interactive-outline-hover"
                  >
                    <div className={`flex items-center flag ${getFlagCountryClassName(option)}`}>
                      <span className="ml-7 block grow-0 group-data-selected:font-semibold">{option.label}</span>
                    </div>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>
        </div>
      </div>
      <FieldValidationMessage message={props.validatemessage} />
    </div>
  );
}

const stripLeadingZero = (phoneNumber: string): string => {
  return phoneNumber.startsWith('0') ? phoneNumber.slice(1) : phoneNumber;
};

const extractCountryCodeAndNumber = (
  fullNumber: string,
  enhancedCountryOptions: EnhancedCountryOption[]
): {
  countryCode: string;
  phoneNumberWithoutCountryCode: string;
} => {
  if (!fullNumber) {
    return {
      countryCode: '',
      phoneNumberWithoutCountryCode: '',
    };
  }
  // Sort country codes by length (descending) to check longest matches first
  // This prevents +1 from matching when the actual code is +12 or longer
  const sortedCountryCodes = [...enhancedCountryOptions].sort((a, b) => b.value.length - a.value.length);
  for (const country of sortedCountryCodes) {
    if (fullNumber.startsWith(country.value)) {
      const countryCode = country.value;
      const phoneNumberWithoutCountryCode = fullNumber.slice(country.value.length);
      return {
        countryCode,
        phoneNumberWithoutCountryCode,
      };
    }
  }
  return {
    countryCode: '',
    phoneNumberWithoutCountryCode: fullNumber,
  };
};

export default Phone;
